import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import Badge, { type BadgeProps } from "@/v2-backoffice-client/components/badge"
import LicensePlanMigrationRowToolbar from "@/v2-backoffice-client/license/plan/view/LicensePlanMigrationRowToolbar"
import { usePlanMigrationsQuery } from "@/v2-backoffice-client/license/plan/view/license.plan.migrations.query"
import { getLicenseStatusVisual } from "@/v2-backoffice-client/license/license.utils"
import {
  PLAN_MIGRATION_OUTCOME_ENDED,
  PLAN_MIGRATION_OUTCOME_MOVED,
  PLAN_MIGRATION_STATUS_AGREED,
  PLAN_MIGRATION_STATUS_DECLINED,
  PLAN_MIGRATION_STATUS_IN_DIALOGUE,
} from "@/v2-backoffice-client/license/license.constants"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import type { PlanMigrationsQuery } from "@/v2-backoffice-client/license/plan/view/__types__/license.plan.migrations.query"

const WORK_STATE_OPEN = "Still on license"
const WORK_STATE_MOVED = "Moved"
const WORK_STATE_ENDED = "Ended"

// The one display state per row: the OUTCOME when the license has left the
// license (nothing to work anymore), otherwise the stored conversation state,
// with no row yet reading as Not started.
type MigrationRow = PlanMigrationsQuery["getPlanMigrations"][number] & {
  WorkState:
    | typeof WORK_STATE_OPEN
    | typeof WORK_STATE_MOVED
    | typeof WORK_STATE_ENDED
  MigrationStatusLabel: string
}

function getMigrationStatusVariant(row: MigrationRow): BadgeProps["variant"] {
  if (row.OutcomeCode === PLAN_MIGRATION_OUTCOME_MOVED) return "positive-strong"
  if (row.OutcomeCode === PLAN_MIGRATION_OUTCOME_ENDED) return "neutral"
  switch (row.PlanMigrationStatusCode) {
    case PLAN_MIGRATION_STATUS_IN_DIALOGUE:
      return "warning"
    case PLAN_MIGRATION_STATUS_AGREED:
      return "positive"
    case PLAN_MIGRATION_STATUS_DECLINED:
      return "critical"
    default:
      return "outline"
  }
}

function getMigrationStatusLabel(
  row: PlanMigrationsQuery["getPlanMigrations"][number],
): string {
  if (row.OutcomeCode === PLAN_MIGRATION_OUTCOME_MOVED) return WORK_STATE_MOVED
  if (row.OutcomeCode === PLAN_MIGRATION_OUTCOME_ENDED) return WORK_STATE_ENDED
  return row.PlanMigrationStatusName ?? "Not started"
}

function createMigrationColumns() {
  return createColumns<MigrationRow>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      CustomerName: {
        id: "CustomerName",
        label: "Customer",
        defaultSize: 190,
        // Push onto the sheet stack — license → customer and back. App-created
        // customers without a HubSpot id can't deep-link.
        to: ({ row }) =>
          row.original.ExternalCustomerId
            ? `>>?v=customer:${row.original.ExternalCustomerId}`
            : undefined,
      },
      MigrationStatusLabel: {
        id: "MigrationStatusLabel",
        label: "Migration",
        description:
          "Where the renegotiation stands. Moved/Ended are derived from the customer's license itself — executing the change in Fenerum is what completes a migration, never a click here.",
        defaultSize: 110,
        enableGrouping: true,
        renderCell({ row }) {
          return (
            <Badge variant={getMigrationStatusVariant(row.original)}>
              {row.original.MigrationStatusLabel}
            </Badge>
          )
        },
      },
      ToPlanName: {
        id: "ToPlanName",
        label: "Agreed Target",
        description:
          "The license this customer agreed to move to — may differ from the license's suggested successor.",
        defaultSize: 150,
        renderCell({ row }) {
          const { ToPlanId, ToPlanName } = row.original
          if (!ToPlanId || !ToPlanName) return null
          return ToPlanName
        },
        to: ({ row }) =>
          row.original.ToPlanId
            ? `>>?v=plan:${row.original.ToPlanId}`
            : undefined,
      },
      Mrr: {
        id: "Mrr",
        label: "MRR",
        description:
          "What this customer bills on the deprecated license's terms — who to call first.",
        defaultSize: 90,
        renderCell({ row }) {
          const value = row.original.Mrr
          if (value == null) return null
          return (
            <span className="tabular-nums">
              {formatCompactAmount(value, null)}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.Mrr ?? ""
        },
      },
      RenewsAt: {
        id: "RenewsAt",
        label: "Renews",
        description:
          "The natural moment to land the migration — schedule the Fenerum change at this renewal.",
        defaultSize: 90,
        renderCell({ row }) {
          const value = row.original.RenewsAt
          return value ? formatDateString(value) : null
        },
      },
      ProductName: {
        id: "ProductName",
        label: "Product",
        defaultSize: 100,
        enableGrouping: true,
        renderCell({ row }) {
          return <Badge size="sm">{row.original.ProductName}</Badge>
        },
        exportValue({ row }) {
          return row.original.ProductName
        },
      },
      LicenseStatusName: {
        id: "LicenseStatusName",
        label: "License status",
        defaultSize: 100,
        enableGrouping: true,
        renderCell({ row }) {
          // Same hue and icon as the license pills everywhere else.
          const visual = getLicenseStatusVisual(row.original.LicenseStatusCode)
          return (
            <Badge variant={visual.variant} iconSrc={visual.iconSrc}>
              {row.original.LicenseStatusName}
            </Badge>
          )
        },
      },
      Seats: {
        id: "Seats",
        label: "Seats",
        defaultSize: 60,
        defaultHidden: true,
      },
      Note: {
        id: "Note",
        label: "Note",
        defaultSize: 220,
        renderCell({ row }) {
          const value = row.original.Note
          if (!value) return null
          return <span className="truncate text-muted-foreground">{value}</span>
        },
      },
      CurrentPlanName: {
        id: "CurrentPlanName",
        label: "Now On",
        description:
          "Where the customer actually sits — for moved rows, the license they landed on.",
        defaultSize: 140,
        defaultHidden: true,
        renderCell({ row }) {
          const { CurrentPlanId, CurrentPlanName, OutcomeCode } = row.original
          if (OutcomeCode !== PLAN_MIGRATION_OUTCOME_MOVED) return null
          if (!CurrentPlanId || !CurrentPlanName) return null
          return CurrentPlanName
        },
      },
      MigrationUpdatedByUserName: {
        id: "MigrationUpdatedByUserName",
        label: "Updated By",
        defaultSize: 130,
        defaultHidden: true,
      },
      MigrationUpdatedAt: {
        id: "MigrationUpdatedAt",
        label: "Updated",
        defaultSize: 90,
        defaultHidden: true,
        renderCell({ row }) {
          const value = row.original.MigrationUpdatedAt
          return value ? formatDateString(String(value)) : null
        },
      },
      LicenseId: {
        id: "LicenseId",
        label: "License Id",
        defaultSize: 70,
        defaultHidden: true,
      },
    },
  })
}

export type LicensePlanMigrationTableProps = Omit<
  TableProps<MigrationRow>,
  "data" | "columns"
> & {
  planId: number
  /** Seeds the row editor's target with the license's suggested successor. */
  suggestedToPlanId?: number | null
  onRefresh?(): void
}

// The Migration tab of a deprecated license: the renegotiation worklist. Every
// customer still on the license is a row (untouched customers first, most money
// first — the proc's order), and rows whose license already moved or ended
// stay visible as the progress record. Owns the worklist query; the pills
// double as the progress summary.
function LicensePlanMigrationTable(props: LicensePlanMigrationTableProps) {
  const { className, planId, suggestedToPlanId, onRefresh, ...tableProps } =
    props

  const { data, previousData, loading, error, refetch } =
    usePlanMigrationsQuery({
      variables: { planId },
    })
  // previousData keeps rows rendered through post-save evictions.
  const migrations = (data ?? previousData)?.getPlanMigrations ?? []

  const rows = useMemo<MigrationRow[]>(
    () =>
      migrations.map((migration) => ({
        ...migration,
        WorkState:
          migration.OutcomeCode === PLAN_MIGRATION_OUTCOME_MOVED
            ? WORK_STATE_MOVED
            : migration.OutcomeCode === PLAN_MIGRATION_OUTCOME_ENDED
              ? WORK_STATE_ENDED
              : WORK_STATE_OPEN,
        MigrationStatusLabel: getMigrationStatusLabel(migration),
      })),
    [migrations],
  )

  const openCount = rows.filter(
    (row) => row.WorkState === WORK_STATE_OPEN,
  ).length
  const movedCount = rows.filter(
    (row) => row.WorkState === WORK_STATE_MOVED,
  ).length

  const columns = useMemo(() => createMigrationColumns(), [])

  return (
    <Table
      className={cn("license-plan-migration-table", className)}
      tableId="license-plan-migration-table"
      data={rows}
      columns={columns}
      {...tableProps}
    >
      <Table.Toolbar>
        {/* The pills ARE the progress summary: open worklist vs landed vs
            churned, with counts. Namespaced param — the panel hosts several
            tabs. */}
        <Table.StatusFilter
          column="WorkState"
          urlParam="migration-state"
          isLoading={loading}
        >
          <Table.StatusFilter.Option value={null} count={rows.length}>
            All
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={0}
            filterValue={WORK_STATE_OPEN}
            variant="destructive"
            iconSrc="exclamation"
            count={openCount}
          >
            Still on license
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={1}
            filterValue={WORK_STATE_MOVED}
            variant="success"
            iconSrc="check-circle"
            count={movedCount}
          >
            Moved
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={2}
            filterValue={WORK_STATE_ENDED}
            count={rows.length - openCount - movedCount}
          >
            Ended
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />
        <Table.Search
          column="CustomerName"
          placeholder="Search by customer name..."
        />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu
          onRefresh={() => {
            void refetch()
            onRefresh?.()
          }}
          exportFileName={`license-${planId}-migrations`}
        />
      </Table.Toolbar>

      <Table.FilterList />

      <Table.RowToolbar<MigrationRow> column="CustomerName">
        {(migration) => (
          <LicensePlanMigrationRowToolbar
            migration={migration}
            fromPlanId={planId}
            suggestedToPlanId={suggestedToPlanId}
          />
        )}
      </Table.RowToolbar>

      <Table.Content isLoading={loading} error={error} />
    </Table>
  )
}

export default LicensePlanMigrationTable
